import React from 'react';
import PropTypes from 'prop-types';
import Checkbox from 'semantic-ui-react/dist/commonjs/modules/Checkbox';
import _isEqual from 'lodash/isEqual';
import Form from '../../components/Form';
import Radio from '../../components/Radio';
import Button from '../../components/Button';
import MessagingDiff from './MessagingDiff';
import MessagingSame from './MessagingSame';
import { convertChars, replaceLineBreaksWithSpaces } from '../../utils/string';
import UnsavedChangesPrompt from '../../components/UnsavedChangesPrompt';
import './styles.css';

export const MessagingOptions = {
  NOMESSAGE: 'NOMESSAGE',
  SAMEFORALL: 'SAMEFORALL',
  DIFFERENTIATE: 'DIFFERENTIATE',
};

export const MessagingErrors = {
  CHECK_MESSAGES: 'CHECK_MESSAGES',
};

function isMessageFieldsDirty(entry, original) {
  const isDefaultMessageDirty = entry.defaultMessage !== original.defaultMessage;
  if (isDefaultMessageDirty) return true;

  const isSameMessagingDirty = (entry.type === MessagingOptions.SAMEFORALL) && entry.messaging !== original.defaultMessage;
  const isDiffMessagingDirty = (entry.type === MessagingOptions.DIFFERENTIATE) &&
    !_isEqual(entry.messaging, original.messages);

  return isDiffMessagingDirty || isSameMessagingDirty;
}

export const formatMessage = (value) => {
  if (typeof value === 'string') {
    return replaceLineBreaksWithSpaces(value);
  } else if (Array.isArray(value)) {
    return value.map((item) => {
      if (typeof item === 'object' && typeof item.message !== 'undefined') {
        return {
          ...item,
          message: replaceLineBreaksWithSpaces(item.message),
        };
      }
      return item;
    });
  }
  return value;
};

class ComposeMessaging extends React.Component {
  static propTypes = {
    options: PropTypes.object.isRequired,
    list: PropTypes.array.isRequired,
    refList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
    onSubmit: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
    allowDisable: PropTypes.bool,
    refsPerEntry: PropTypes.bool,
    children: PropTypes.any,
    labelStyle: PropTypes.object,
  };

  static defaultProps = {
    allowDisable: false,
    refsPerEntry: false,
  };

  static getDerivedStateFromProps(props, state) {
    const { prevList } = state;
    const { list } = props;
    const newState = {};

    if (list && prevList !== list) {
      newState.list = [].concat(list);
      newState.prevList = list;
    }
    return newState;
  }

  state = {
    list: [],
    prevList: null,
  };

  handleTypeChange = (e, props) => {
    const { name: oname, value } = props;
    const id = oname.split('.')[0];
    // eslint-disable-next-line react/no-access-state-in-setstate
    const newList = this.state.list.map((co) => {
      if (co.id === id) {
        const { type } = co;
        /* istanbul ignore else */
        if (type === MessagingOptions.DIFFERENTIATE) {
          co.messages = co.messaging;
        } else if (type === MessagingOptions.SAMEFORALL) {
          co.defaultMessage = co.messaging;
        }

        if (value === MessagingOptions.DIFFERENTIATE) {
          co.messaging = co.messages;
        } else if (value === MessagingOptions.SAMEFORALL) {
          co.messaging = co.defaultMessage;
        }
        return {
          ...co,
          type: value,
        };
      }
      return co;
    });

    this.setState({
      list: newList,
    });
  }

  handleMessageChange = (e, props) => {
    const [id, field] = props.name.split('.');
    // eslint-disable-next-line react/no-access-state-in-setstate
    const newList = this.state.list.map((co) => {
      if (co.id === id) {
        const newValue = props.type === 'checkbox' ? !props.checked : formatMessage(props.value);
        return {
          ...co,
          [field]: newValue,
        };
      }
      return co;
    });

    this.setState({
      list: newList,
    });
  }

  _validateForm = () => {
    const { onError } = this.props;

    const { list } = this.state;
    let shouldCheck = false;

    const cleaned = list.reduce((acc, entry) => {
      const { type, messaging } = entry;

      let newMessaging = null;

      switch (type) {
        case MessagingOptions.SAMEFORALL: {
          const temp = messaging && convertChars(messaging);
          /* istanbul ignore else */
          if (temp) {
            newMessaging = temp.text;
            shouldCheck = temp.shouldCheck;
          }
        }
          break;
        /* eslint-disable no-lone-blocks */
        case MessagingOptions.DIFFERENTIATE: {
          newMessaging = Array.isArray(messaging) && messaging.map((msg) => {
            const { message } = msg;
            const temp = convertChars(message);
            shouldCheck = temp.shouldCheck;

            return {
              ...msg,
              message: temp.text,
            };
          });
        }
          break;
        /* eslint-enable no-lone-blocks */
      }

      const cleanedEntry = {
        ...entry,
        messaging: newMessaging || '',
      };
      acc.push(cleanedEntry);
      return acc;
    }, []);

    this.setState({
      list: cleaned,
    });

    if (shouldCheck) {
      onError({ type: MessagingErrors.CHECK_MESSAGES });
    } else {
      return cleaned;
    }

    return null;
  }

  handleSubmit = (e) => {
    const { onSubmit } = this.props;
    e.preventDefault();
    const result = this._validateForm();
    if (result) {
      onSubmit(result);
    }
  }

  promptSaveChanges = () => {
    const { onSubmit } = this.props;
    const result = this._validateForm();
    if (result) {
      return onSubmit(result);
    }
    /*
     * This rejection is to let UnsavedChangesPrompt that should not change the page
     * The value is not used for any purpose
     */
    return Promise.reject(new Error('Invalid data'));
  }

  isDirty() {
    const { list } = this.state;
    const { list: original, allowDisable } = this.props;

    const dirty = list.some((entry, index) => {
      if (allowDisable) {
        const disabledDirty = entry.disabled !== original[index].disabled;
        if (disabledDirty) return true;
      }

      const typeDirty = entry.type !== original[index].type;
      if (typeDirty) return true;

      return isMessageFieldsDirty(entry, original[index]);
    });
    return dirty;
  }

  renderMessaging(entry) {
    const { labelStyle, allowDisable } = this.props;
    const { id, name, disabled, extraName } = entry;
    const ariaLabel = `enable/disable messaging for ${name}`;
    const messagingEntryName = `messaging-entry-${name}`;
    return (
      <div styleName="messaging-entry" key={id} data-cy={messagingEntryName}>
        {
          allowDisable &&
          (
          <Checkbox
            styleName="messaging-disable"
            type="checkbox"
            checked={!disabled}
            name={`${id}.disabled`}
            onChange={this.handleMessageChange}
            aria-label={ariaLabel}
          />
          )
        }
        <h3 styleName="messaging-title" style={labelStyle}>{name} {extraName}</h3>
        <div styleName="messaging-entry-content">
          { (!allowDisable || !disabled) && this.renderEntryOptions(entry) }
          { (!allowDisable || !disabled) && this.renderEntryMessaging(entry) }
        </div>
      </div>
    );
  }

  renderEntryMessaging(entry) {
    const { refList, refsPerEntry } = this.props;
    const { type, messaging } = entry;
    let Renderer = null;
    switch (type) {
      case MessagingOptions.SAMEFORALL:
        Renderer = MessagingSame;
        break;
      case MessagingOptions.DIFFERENTIATE:
        Renderer = MessagingDiff;
        break;
      default: {
        return null;
      }
    }

    const auxList = refsPerEntry ? (refList[entry.id] || []): refList;

    return <Renderer entryId={entry.id} type={type} data={messaging} onChange={this.handleMessageChange} list={auxList} />;
  }

  renderEntryOptions(entry) {
    const { options } = this.props;
    const { id, type, name } = entry;
    const ariaLabel = `Messaging options for ${name}`;
    const messagingOptionsCY = `message-options-${name}`;
    return (
      <fieldset styleName="messaging-options" key={ariaLabel} aria-label={ariaLabel} data-cy={messagingOptionsCY}>
        <legend>{ariaLabel}</legend>
        { Object.keys(MessagingOptions).map((key) => {
          const oname = MessagingOptions[key];
          const label = options[oname];
          const fieldName = `${id}.${oname}`;
          const oariaLabel = `messaging type for ${oname}`;
          const ttype = type || MessagingOptions.NOMESSAGE;
          return (
            <Form.Field key={fieldName}>
              <Radio
                label={label}
                name={fieldName}
                value={oname}
                checked={oname === ttype}
                onChange={this.handleTypeChange}
                aria-label={oariaLabel}
              />
            </Form.Field>
          );
        })}
      </fieldset>
    );
  }

  render() {
    const { list } = this.state;
    const isDirty = this.isDirty();

    return (
      <React.Fragment>
        <Form onSubmit={this.handleSubmit}>
          { list.map((entry) => this.renderMessaging(entry)) }

          { this.props.children }

          <div styleName="form-footer">
            <Form.Field>
              <Button primary color="teal" type="submit" date-cy="compose-messaging-saveBtn">Save</Button>
            </Form.Field>
          </div>
        </Form>
        <UnsavedChangesPrompt
          when={isDirty}
          saveChanges={this.promptSaveChanges}
          closeable
        />
      </React.Fragment>
    );
  }
}

export default ComposeMessaging;
